import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { toast } from "sonner";
import { getCohort, reconcileCohort } from "@/api/close-cohorts";
import { Page } from "@/components/page";

/**
 * Phase C. Dry-run first, always — the button that writes to Close is
 * disabled until a dry run has been read, because a reconcile PUTs a lead
 * status onto thousands of CRM records and there is no undo.
 */
export function CohortDetailPage() {
  const { cohortId } = useParams({ from: "/app/cohorts/$cohortId" });
  const qc = useQueryClient();
  const [statusId, setStatusId] = useState("");
  const [sawDryRun, setSawDryRun] = useState(false);

  const cohort = useQuery({
    queryKey: ["cohort", cohortId],
    queryFn: () => getCohort(cohortId),
    // A reconcile runs in a server-side thread; poll while it's in flight.
    refetchInterval: (q) =>
      q.state.data?.reconcile_status === "running" ? 2000 : false,
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      reconcileCohort(cohortId, statusId.trim(), dryRun),
    onSuccess: (_r, dryRun) => {
      if (dryRun) setSawDryRun(true);
      toast.success(dryRun ? "Dry run started" : "Reconcile started");
      qc.invalidateQueries({ queryKey: ["cohort", cohortId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const c = cohort.data;
  const busy = c?.reconcile_status === "running" || run.isPending;
  const outcomes = Object.entries(c?.outcomes ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <Page title={c?.name ?? "Cohort"}>
      <section className="card p-4">
        <dl className="grid gap-1 text-sm sm:grid-cols-2">
          <Row label="Leads" value={String(c?.lead_count ?? "—")} />
          <Row label="Phase B run" value={c?.phase_b_run_id ?? "not scanned"} />
          <Row label="Reconcile" value={c?.reconcile_status ?? "never run"} />
          <Row
            label="Checks evaluated"
            value={c?.check_names?.join(", ") || "—"}
          />
          <Row label="Query" value={c?.query ?? ""} mono />
        </dl>
        {c?.reconcile_error && (
          <p className="mt-2 text-sm text-red-600">{c.reconcile_error}</p>
        )}
      </section>

      <section className="card p-4 mt-4">
        <h2 className="text-sm font-semibold mb-3">Reconcile</h2>
        <label className="label">New lead status id</label>
        <input
          className="input font-mono text-xs"
          value={statusId}
          onChange={(e) => setStatusId(e.target.value)}
          placeholder="stat_…"
        />
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            className="btn"
            disabled={!statusId.trim() || busy}
            onClick={() => run.mutate(true)}
          >
            Dry run
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!statusId.trim() || busy || !sawDryRun}
            onClick={() => run.mutate(false)}
          >
            Write to Close
          </button>
          {!sawDryRun && (
            <span className="text-xs text-muted-foreground">
              Dry-run first — writing has no undo.
            </span>
          )}
        </div>
      </section>

      {outcomes.length > 0 && (
        <section className="card p-4 mt-4">
          <h2 className="text-sm font-semibold mb-3">Outcomes</h2>
          <table className="w-full text-sm">
            <tbody>
              {outcomes.map(([bucket, n]) => (
                <tr key={bucket} className="border-t">
                  <td className="py-1 font-mono text-xs">{bucket}</td>
                  <td className="text-right tabular-nums">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-2">
            Only <code>updated</code> and <code>skipped_lead_gone</code> are
            final; everything else is re-resolved by the next reconcile.
          </p>
        </section>
      )}
    </Page>
  );
}

function Row(
  { label, value, mono }: { label: string; value: string; mono?: boolean },
) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-xs break-all" : ""}>{value}</dd>
    </div>
  );
}
